const FILTER_ENGINE_PATH = /\/(?:filter-fan(?:-export)?|filter-criteria)\/?$/

export const FILTER_ENGINE_MODES = ['legacy', 'filter2', 'shadow'] as const

export type FilterEngineMode = (typeof FILTER_ENGINE_MODES)[number]

function getChargebeeFilterEngineOverride(
  features: unknown,
): Exclude<FilterEngineMode, 'legacy'> | null {
  if (Array.isArray(features)) {
    const hasEnabledFeature = (featureId: 'filter2' | 'shadow') => {
      return features.some((feature) => {
        if (typeof feature === 'string') return feature === featureId
        return feature?.id === featureId && feature?.enabled !== false
      })
    }

    if (hasEnabledFeature('filter2')) return 'filter2'
    if (hasEnabledFeature('shadow')) return 'shadow'
    return null
  }

  if (features && typeof features === 'object') {
    const enabledFeatureIds = Object.values(features)
      .filter(feature => feature?.enabled !== false)
      .map(feature => feature?.id)

    if (enabledFeatureIds.includes('filter2')) return 'filter2'
    if (enabledFeatureIds.includes('shadow')) return 'shadow'
  }

  return null
}

function canUseFilter2(): boolean {
  const nuxtApp = (window?.useNuxtApp?.() || {}) as any
  const devFeaturesEnabled = nuxtApp?.$config?.public?.arDevFeatures === true
  const sessionFeatures = nuxtApp?.$store?.state?.authModule?.account?.features

  return (
    devFeaturesEnabled
    || getChargebeeFilterEngineOverride(sessionFeatures) === 'filter2'
  )
}

function getConfiguredFilterEngine(): FilterEngineMode {
  const nuxtApp = (window?.useNuxtApp?.() || {}) as any
  return (
    normalizeFilterEngineMode(
      nuxtApp?.$config?.public?.arAudienceFilterEngine,
    ) || 'legacy'
  )
}

export function getEffectiveFilterEngine(
  requestedMode?: string | null,
): FilterEngineMode {
  const nuxtApp = (window?.useNuxtApp?.() || {}) as any
  const sessionFeatures = nuxtApp?.$store?.state?.authModule?.account?.features
  const chargebeeOverride = getChargebeeFilterEngineOverride(sessionFeatures)

  if (chargebeeOverride) {
    return chargebeeOverride
  }

  if (!canUseFilter2()) {
    return 'legacy'
  }

  return (
    normalizeFilterEngineMode(requestedMode) || getConfiguredFilterEngine()
  )
}

export function normalizeFilterEngineMode(
  value?: string | null,
): FilterEngineMode | null {
  if (!value) return null

  const normalizedValue = value.trim().toLowerCase()
  return FILTER_ENGINE_MODES.includes(normalizedValue as FilterEngineMode)
    ? (normalizedValue as FilterEngineMode)
    : null
}

export function addFilterEngineParam(
  url: string,
  method: string,
  params: Record<string, unknown> = {},
): Record<string, unknown> {
  if (method.toUpperCase() !== 'GET' || !FILTER_ENGINE_PATH.test(url)) {
    return params
  }

  const { 'filter-engine': filterEngineKebab, ...rest } = params

  return {
    ...rest,
    filterEngine: getEffectiveFilterEngine(
      String(params.filterEngine ?? filterEngineKebab ?? ''),
    ),
  }
}
